import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { CardShell, asText } from './shared';
import Markdown from './Markdown';

interface CardItem {
  titulo?: unknown;
  subtitulo?: unknown;
  texto?: unknown;
  destaque?: unknown;
  etiquetas?: unknown[];
  atributos?: { rotulo?: unknown; valor?: unknown }[];
  link?: unknown;
  link_rotulo?: unknown;
}

export interface CardsSpec {
  tipo: 'cartoes';
  titulo?: string;
  layout?: 'grade' | 'carrossel' | 'lista';
  numerado?: boolean;
  itens?: CardItem[];
  fonte?: string;
}

// Sem layout explícito: grade até 4 itens, carrossel acima disso.
const GRID_MAX = 4;
const SAFE_LINK = /^https?:\/\//i;

function ItemCard({ item, index, numbered, compact }: { item: CardItem; index: number; numbered: boolean; compact?: boolean }) {
  const link = asText(item.link);
  const tags = (item.etiquetas || []).map(asText).filter(Boolean);
  const attrs = (item.atributos || []).filter((a) => a && asText(a.rotulo));
  return (
    <Item $compact={!!compact}>
      {numbered && <NumBadge>{index + 1}</NumBadge>}
      <ItemBody>
        {tags.length > 0 && <Tags>{tags.map((t) => <Tag key={t}>{t}</Tag>)}</Tags>}
        {item.titulo !== undefined && <ItemTitle>{asText(item.titulo)}</ItemTitle>}
        {item.subtitulo !== undefined && <Subtitle>{asText(item.subtitulo)}</Subtitle>}
        {item.destaque !== undefined && <Highlight>{asText(item.destaque)}</Highlight>}
        {item.texto !== undefined && <Markdown>{asText(item.texto)}</Markdown>}
        {attrs.length > 0 && (
          <Attrs>
            {attrs.map((a, i) => (
              <React.Fragment key={i}>
                <dt>{asText(a.rotulo)}</dt>
                <dd>{asText(a.valor)}</dd>
              </React.Fragment>
            ))}
          </Attrs>
        )}
        {SAFE_LINK.test(link) && (
          <ItemLink href={link} target="_blank" rel="noopener noreferrer">
            {asText(item.link_rotulo) || 'Abrir'} <OpenInNewRoundedIcon style={{ fontSize: 13 }} />
          </ItemLink>
        )}
      </ItemBody>
    </Item>
  );
}

function Carousel({ items, numbered }: { items: CardItem[]; numbered: boolean }) {
  const track = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ first: 0, atStart: true, atEnd: false });

  const update = () => {
    const el = track.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    const step = card ? card.offsetWidth + 12 : el.clientWidth;
    setPos({
      first: Math.round(el.scrollLeft / step),
      atStart: el.scrollLeft <= 2,
      atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
    });
  };
  useEffect(update, [items.length]);

  const go = (dir: 1 | -1) => {
    const el = track.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    el.scrollBy({ left: dir * (card ? card.offsetWidth + 12 : el.clientWidth * 0.8), behavior: 'smooth' });
  };

  return (
    <>
      <Track ref={track} onScroll={update} role="list">
        {items.map((it, i) => (
          <Slide key={i} role="listitem"><ItemCard item={it} index={i} numbered={numbered} /></Slide>
        ))}
      </Track>
      <Controls>
        <Counter>{Math.min(pos.first + 1, items.length)} de {items.length}</Counter>
        <NavButton type="button" onClick={() => go(-1)} disabled={pos.atStart} aria-label="Anterior">
          <ChevronLeftRoundedIcon style={{ fontSize: 20 }} />
        </NavButton>
        <NavButton type="button" onClick={() => go(1)} disabled={pos.atEnd} aria-label="Próximo">
          <ChevronRightRoundedIcon style={{ fontSize: 20 }} />
        </NavButton>
      </Controls>
    </>
  );
}

function Cards({ spec }: { spec: CardsSpec }) {
  const items = (spec.itens || []).filter((it) => it && typeof it === 'object');
  if (!items.length) return null;
  const layout = spec.layout || (items.length > GRID_MAX ? 'carrossel' : 'grade');
  const numbered = !!spec.numerado;
  return (
    <CardShell title={spec.titulo} source={spec.fonte} label="cartoes">
      {layout === 'carrossel' ? (
        <Carousel items={items} numbered={numbered} />
      ) : layout === 'lista' ? (
        <ListLayout role="list">
          {items.map((it, i) => (
            <div key={i} role="listitem"><ItemCard item={it} index={i} numbered={numbered} compact /></div>
          ))}
        </ListLayout>
      ) : (
        <Grid role="list">
          {items.map((it, i) => (
            <div key={i} role="listitem"><ItemCard item={it} index={i} numbered={numbered} /></div>
          ))}
        </Grid>
      )}
    </CardShell>
  );
}

export default Cards;

const accent = ({ theme }: any) => theme.prompt_accent_color || '#1a3c34';
const edge = ({ theme }: any) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.09)');

const Item = styled.div<{ $compact: boolean }>`
  display: flex;
  gap: 12px;
  height: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${edge};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff')};
  ${({ $compact }) => $compact && css`padding: 10px 12px;`}
`;

const ItemBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const NumBadge = styled.span`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 600;
  color: #fff;
  background: ${accent};
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Tag = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 9px;
  color: ${accent};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(93,168,138,0.15)' : 'rgba(26,60,52,0.08)')};
`;

const ItemTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const Subtitle = styled.div`
  font-size: 0.78rem;
  opacity: 0.65;
`;

const Highlight = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${accent};
  font-variant-numeric: tabular-nums;
`;

const Attrs = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 3px 10px;
  margin: 4px 0 0;
  font-size: 0.8rem;
  dt { opacity: 0.6; }
  dd { margin: 0; font-weight: 500; }
`;

const ItemLink = styled.a`
  margin-top: auto;
  padding-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.8rem;
  font-weight: 500;
  color: ${accent};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px;
`;

const ListLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Track = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  padding-bottom: 2px;
  &::-webkit-scrollbar { display: none; }
`;

const Slide = styled.div`
  flex: 0 0 min(78%, 260px);
  scroll-snap-align: start;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 8px;
`;

const Counter = styled.span`
  font-size: 0.74rem;
  opacity: 0.6;
  margin-right: auto;
  font-variant-numeric: tabular-nums;
`;

const NavButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid ${edge};
  &:hover:not(:disabled) { border-color: ${accent}; color: ${accent}; }
  &:disabled { opacity: 0.3; cursor: default; }
  &:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }
`;
